<template>
	<div class="org-sidebar">
		<div class="org-sidebar-container">
			<div class="org-sidebar-header">
				<ProfilePic :user="org" size="28px" />
				<div class="org-sidebar-header-info">
					<div class="org-name" v-text="org.name" />
					<div class="user-name" v-text="user.fullname" />
				</div>
			</div>
			<div class="org-sidebar-content">
				<div class="org-sidebar-links">
					<RouterLink
						v-for="link in links"
						:key="link.name"
						:to="{ name: link.name }"
						class="sidebar-link"
					>
						<component :is="link.icon" fixedWidth />
						{{ $t(link.label) }}
					</RouterLink>
				</div>
				<div class="org-sidebar-footer">
					<RouterLink
						:to="{ name: 'mainsettings', params: { id: org.id } }"
						class="sidebar-link"
					>
						<ArrowFromBracketIcon fixedWidth left />
						{{ $t('admin_sidebar_back_label') }}
					</RouterLink>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import { ProfilePic } from '@/components/common'
import {
	HouseIcon,
	UserIcon,
	GroupsIcon,
	CategoriesIcon,
	ChartIcon,
	LockIcon,
	CircleQuestionIcon,
	ArrowFromBracketIcon,
	FileArrowUpIcon,
	GearIcon,
	IntegrationsIcon,
} from '@/icons'
import { getMyUser, getOrganization } from '@/store'

export default {
	name: 'Sidebar',
	components: {
		ProfilePic,
		CircleQuestionIcon,
		ArrowFromBracketIcon,
		FileArrowUpIcon,
		GearIcon,
		IntegrationsIcon,
	},
	props: {},
	data() {
		return {
			user: getMyUser() || {},
			org: getOrganization() || {},
			links: [
				{
					label: 'admin_sidebar_overview_label',
					name: 'overview',
					icon: markRaw(HouseIcon),
				},
				{
					label: 'admin_people_title',
					name: 'users',
					icon: markRaw(UserIcon),
				},
				{
					label: 'admin_groups_title',
					name: 'groups',
					icon: markRaw(GroupsIcon),
				},
				{
					label: 'admin_categories_title',
					name: 'categories',
					icon: markRaw(CategoriesIcon),
				},
				{
					label: 'admin_overview_reports_title',
					name: 'reports',
					icon: markRaw(ChartIcon),
				},
				{
					label: 'admin_sidebar_permissions_label',
					name: 'permissions',
					icon: markRaw(LockIcon),
				},
				{
					label: 'settings_title',
					name: 'settings',
					icon: markRaw(GearIcon),
				},
			],
		}
	},
	mounted() {
		this.$onEventBus('user-update', () => {
			this.user = getMyUser() || {}
			this.org = getOrganization() || {}
		})

		if (this.org.imports_enabled) {
			this.links.push({
				label: 'admin_sidebar_imports_label',
				name: 'imports',
				icon: markRaw(FileArrowUpIcon),
			})
		}

		if (this.org.integrations_enabled) {
			this.links.push({
				label: 'admin_overview_integrations_title',
				name: 'integrations',
				icon: markRaw(IntegrationsIcon),
			})
		}
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$collapsed-width: 64px;
$expanded-width: 220px;
$header-height: 58px;
$sidebar-back: #141720;
$link-active: #2b2f38;

.org-sidebar {
	position: relative;
	@include flex-column;
	width: $collapsed-width;
	.org-sidebar-container {
		z-index: 2;
		position: absolute;
		width: $collapsed-width;
		transition: width 0.15s ease-in-out;
		background: $sidebar-back;
		height: 100vh;
		top: 0;
		left: 0;
		&:hover {
			width: $expanded-width;
			.org-sidebar-header-info {
				opacity: 1;
			}
		}
	}
}
.org-sidebar-header {
	display: flex;
	align-items: center;
	padding-left: 18px;
	height: $header-height;
	border-bottom: 1px solid #292d35;
	:deep(.profile-pic-avatar) {
		flex-shrink: 0;
	}
	.org-sidebar-header-info {
		opacity: 0;
		max-width: 100%;
		overflow: hidden;
		margin-left: 12px;
		transition: opacity 0.15s linear;
		.org-name {
			@include ellipsis;
			color: white;
			font-size: 14px;
		}
		.user-name {
			font-size: 12px;
			font-family: Avenir-Book;
			flex-grow: 1;
			@include ellipsis;
			color: var(--neutral-400);
		}
	}
}
.org-sidebar-content {
	@include flex-column;
	justify-content: space-between;
	height: calc(100% - #{$header-height});
	.org-sidebar-links,
	.org-sidebar-footer {
		@include flex-column;
		padding: 12px;
	}
}
.sidebar-link {
	padding: 0 10px;
	height: 40px;
	display: flex;
	align-items: center;
	text-decoration: none;
	font-family: Avenir-Roman;
	color: var(--neutral-400);
	width: 100%;
	overflow: hidden;
	border-radius: 4px;
	margin-bottom: 8px;
	@include ellipsis;
	&:hover,
	&.router-link-active {
		color: white;
		font-family: Avenir-Heavy;
		:deep(svg) {
			path.light {
				display: none;
			}
			path.regular {
				display: inline;
			}
		}
	}
	&.router-link-active {
		background: $link-active !important;
	}
	:deep(.pronto-icon) {
		font-size: 20px;
		margin-right: 20px;
	}
}
</style>
